import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Component, OnInit, Input, HostListener, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';

//* Material
import { MatDialog } from '@angular/material/dialog';

//* RXJS
import { debounceTime, Subject, take, takeUntil } from 'rxjs';

//* Components
import { CartComponent } from '@components/cart/cart.component';
import { SigninComponent } from '@components/signin/signin.component';
import { DeliveryComponent } from '@components/delivery/delivery.component';

//* Models
import { TProductTypeQuantity } from '@models/cart.model';
import { EventCustomData, PixelEvent } from '@models/pixel.model';
import { IClient } from '@models/client.model';

//* Services
import { AddressObserverService } from '@services/address-observer.service';
import { CartObserverService } from '@services/cart-observer.service';
import { CartService } from '@services/cart.service';
import { ListsService } from '@services/lists.service';
import { SnackbarService } from '@services/snackbar.service';
import { ProductsValidationsService, TProductType } from '@services/products-validations.service';
import { MarketingService } from '@services/marketing.service';

//* Shared
import { SKELETON_LIST, DEFAULT_IMAGE_URL } from '@shared/utils';
import { imageHandling } from '@shared/imageHandling';
import { AppStateUser } from 'src/app/state/user/user.reducer';
import { Store } from '@ngrx/store';
import { encryptTextSHA256 } from '@shared/encryptText';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SlicePipe, CurrencyPipe, isPlatformBrowser, CommonModule } from '@angular/common';
import { OrderService } from '@services/order.service';
import { AppStateCart } from 'src/app/state/cart/cart.reducer';
import { MatSelectModule } from '@angular/material/select';
import { Item } from '@models/localstorage.model';

declare let gtag: any;
declare let pintrk: Function;
declare let fbq: Function;

@Component({
  selector: 'app-product-purchase',
  templateUrl: './product-purchase.component.html',
  styleUrls: ['./product-purchase.component.scss'],
  standalone: true,
  imports: [MatProgressSpinnerModule, CommonModule, FormsModule, MatSelectModule, ReactiveFormsModule, CommonModule, MatFormFieldModule, RouterLink, LazyLoadImageModule, MatIconModule, MatButtonModule, SlicePipe, CurrencyPipe, TranslateModule]
})
export class ProductPurchaseComponent implements OnInit, OnDestroy {

  @Input() product: any;
  public storeId: string;
  public displayToolbar = false;
  public defaultImage: string = DEFAULT_IMAGE_URL;
  public client: IClient;
  private subs = new Subject<void>();
  public loading = true;
  public baseGrams = 0;
  public discount = 0;
  public isMobile: boolean;
  public sliceList: string[] = [];
  public madurityList: string[] = [];
  public descriptionActive = 1;
  public loadingProducts = false;
  public productsPromos: any;
  public productQuantityGrams = 50;
  public madurity = new UntypedFormControl();
  public slice = new UntypedFormControl();
  public skeletonList = SKELETON_LIST;
  public carouselProductsHeight = 364;
  public productsAlsoBought: any;
  public showHeart = false;
  public quantityBaseOnInches: number;
  public showSocial = false;
  public weight: number;
  public addingProduct = false;
  public productImageCloudFront: string;
  public displayProductQuantity = false;
  public quantity: number;
  public productQuantity: number;
  public productAdded = false;
  public productType: TProductType;
  public productTypeQuantity: TProductTypeQuantity;
  public imgHa: string;
  public productImage: string;
  public productFromCart: any;
  public cartItems: any[];
  public historicalPrice: number;
  public historicalQuantity: number;
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);
  private productImageFormated: string;
  private clickSubject = new Subject();
  public showSpinner = false;
  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (this.isBrowser) this.displayToolbar = window.innerWidth < 1005;
  }

  constructor(
    private cartService: CartService,
    private cartObserver: CartObserverService,
    private addressObserver: AddressObserverService,
    private dialog: MatDialog,
    private listsService: ListsService,
    private snackbarService: SnackbarService,
    private productsValidationsService: ProductsValidationsService,
    private marketingService: MarketingService,
    private storeUser: Store<AppStateUser>,
    private orderService: OrderService,
    private storeCart: Store<AppStateCart>
  ) { }

  ngOnInit(): void {
    if (this.isBrowser) this.displayToolbar = window.innerWidth < 1005;
    this.storeId = JSON.parse(localStorage.getItem('address') || '{}').store;
    this.getHistoricalData();
    this.setProductData();
    this.getClientData();
    this.sendQuantityListener();

    // this.store.select('cart').pipe(takeUntil(this.subs)).subscribe(({ info }) => {
    //   this.cartItems = info?.items;
    //   const item = this.cartItems.find((item: any) => (item.item_id === this.product.item_id));
    //   if (item) {
    //     this.checkProduct(item);
    //   }
    // });

    // this.productsService.productCardRefresh$.pipe(takeUntil(this.subs)).subscribe(({
    //   next: (item) => {
    //     if (this.product.item_id === item.item_id) {
    //       if (item.quantity === 0) {
    //         this.productAdded = false;
    //         this.setProductData();
    //       } else {
    //         this.checkProduct(item);
    //       }
    //     }
    //   }
    // }));
    this.setLocalCartListener();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.subs.next();
    this.subs.complete();
    this.clickSubject.next('skip');
    this.clickSubject.complete();
  }


  private sendQuantityListener(): void {
    this.clickSubject.pipe(
      debounceTime(600)
    ).subscribe(async (data) => {
      if (data === 'skip') {
        return;
      }
      await this.sendQuantityChange();
    });
  }


  public async sendQuantityChange(): Promise<void> {
    try {
      this.showSpinner = true;
      const { data } = await this.cartService.onHandlingProductQuantity({
        itemId: this.product.item_id ?? this.product.id,
        quantity: Math.round(this.quantity * 1000) / 1000,
        branch_id: this.storeId,
        unit: this.productTypeQuantity,
        variant: this.madurity?.value ? this.madurity?.value : this.slice?.value
      });
      if (data?.message === 'Se ha actualizado la cantidad') {
      } else {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
      this.showSpinner = false;
      this.onEmitReload();
    } catch (error) {
      this.loading = false;
      this.showSpinner = false;
      this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
    }
  }


  private setItemToDefault() {
    this.productQuantity = 1;
    this.productQuantityGrams = 500;
    this.displayProductQuantity = false;
    this.productAdded = false;
  }

  public setLocalCartListener(): void {
    this.storeCart.select('cart').pipe(takeUntil(this.subs)).subscribe(({ info }) => {
      const itemInCart: Item[] = info?.items;
      const foundItem = itemInCart?.find(
        (itemInCart: Item) => itemInCart.item_id === this.product.item_id
      );
      if (foundItem) {
        this.checkProduct(foundItem);
      } else {
        this.setItemToDefault();
      }
    });
  }


  private getHistoricalData(): void {
    this.historicalPrice = this.product.price;
    this.historicalQuantity = this.product.quantity;
  }

  private setProductData(): void {
    const descriptionArr = this.product.short_description.split(' ');
    const desc = descriptionArr.join('-');
    const arrWithoutSlash = desc.split('/');
    if (this.product.image_url) {
      this.imgHa = imageHandling(this.product?.image_url, 'small', '.webp');
      this.productImageFormated = this.imgHa;
    }

    const format = this.product.format.toUpperCase();
    this.product.format = format === 'KG' ? 'KG' : 'PZ';

    this.product.product_url = `/producto/${arrWithoutSlash.join('-').toLowerCase()}-${this.product?.item_id}`;

    this.product.price = Number.parseFloat(this.product.price);
    this.product.regular_price = Number.parseFloat(this.product.regular_price);

    this.productImage = this.productImageFormated;

    this.product.unit = this.product?.unit?.trim().toLowerCase();

    if (this.isBrowser) this.discount = 100 - Math.trunc((this.product.price * 100) / this.product.regular_price);

    this.productType = this.productsValidationsService.productCartTypeValidation(this.product.unit, this.product.weight);

    const lowerVariant = this.product?.variant?.toLowerCase();

    switch (this.productType) {
      case 'grammage':
        this.productTypeQuantity = 'grammage';
        this.productQuantityGrams = this.product?.quantity * 1000;
        this.baseGrams = Number.parseFloat(this.product?.weight) * 1000;
        this.weight = Number.parseFloat(this.product?.weight);
        this.productQuantity = this.baseGrams;
        break;
      case 'pza':
        this.productTypeQuantity = 'pza';
        this.productQuantity = 1;
        break;
      case 'granel':
        this.productTypeQuantity = 'granel';
        this.baseGrams = 50;
        this.weight = 50;
        this.productQuantity = 50;
        this.productQuantityGrams = 50;
        break;
      case 'pza-grammage':
        this.productTypeQuantity = 'pza';
        this.productQuantity = 1;
        this.baseGrams = Number.parseFloat(this.product?.weight) * 1000;
        break;
    }


    if (lowerVariant === 'madurez') {
      this.madurityList = this.product.madurity;
      this.madurity.patchValue('Maduro');
    }
    if (lowerVariant === 'rebanada') {
      this.sliceList = this.product.slice;
      this.slice.patchValue('Rebanada Delgada');
    }
    if (this.product?.productFromCart) {
      this.checkProduct(this.product?.productFromCart);
    }
  }

  public async checkProduct(product: any): Promise<void> {
    this.productQuantity = product?.quantity;
    try {

      if (product.item_id === this.product.item_id) {
        let unit: string;
        this.productFromCart = product;
        this.productAdded = true;
        this.displayProductQuantity = true;
        this.productType = this.productsValidationsService.productCartTypeValidation(this.product.unit, this.product.weight);
        const lowerVariant = this.product?.variant?.toLowerCase();
        switch (this.productType) {
          case 'grammage':
            this.productTypeQuantity = 'grammage';
            this.productQuantityGrams = product?.quantity * 1000;
            this.baseGrams = Number.parseFloat(product?.weight) * 1000;
            this.weight = Number.parseFloat(product?.weight);
            break;
          case 'pza':
            this.productTypeQuantity = 'pza';
            this.productQuantity = product.quantity;
            break;
          case 'granel':
            this.productTypeQuantity = 'granel';
            this.baseGrams = 50;
            this.weight = 50;
            this.productQuantityGrams = product?.quantity * 1000;
            break;
          case 'pza-grammage':
            unit = product.unit.toLowerCase();
            this.baseGrams = Number.parseFloat(product?.weight) * 1000;
            this.weight = Number.parseFloat(product?.weight);
            if (unit === 'grammage') {
              this.productQuantityGrams = product?.quantity * 1000;
              this.productTypeQuantity = 'grammage';
            } else {
              this.productTypeQuantity = 'pza';
              this.productQuantity = product.quantity / Number.parseFloat(product.weight);
              this.productQuantity = Number.parseInt(this.productQuantity.toFixed(2));
            }
            break;
        }
        this.productQuantityGrams = Number.parseInt(this.productQuantityGrams.toFixed(2));
        if (lowerVariant === 'madurez') {
          this.madurityList = this.product.madurity;
          this.madurity.patchValue(product.variant);
        }
        if (lowerVariant === 'rebanada') {
          this.sliceList = this.product.slice;
          this.slice.patchValue(product.variant);
        }

        this.product.comment = product.comment;
      }
    } catch (error) {
    }
  }

  public getClientData(): void {
    this.storeUser.select('user').pipe(takeUntil(this.subs)).subscribe(({ data }) => {
      this.client = data;
      this.storeId = JSON.parse(localStorage.getItem('address') || '{}').store;
    });

  }

  public async onHandlingProductReplacement(): Promise<void> {
    await this.cartService.onHandlingProductReplacement({ itemId: this.product.item_id });
  }

  public setProductQuantity(skipNotify = false): void {
    switch (this.productType) {
      case 'grammage':
      case 'granel':
        this.quantity = this.productQuantityGrams / 1000;
        break;
      case 'pza':
        this.quantity = this.productQuantity;
        break;
      case 'pza-grammage':
        if (this.productTypeQuantity === 'pza') this.quantity = this.productQuantity * Number.parseFloat(this.product.weight);
        if (this.productTypeQuantity === 'grammage') this.quantity = this.productQuantityGrams / 1000;
        break;
    }
    if (!skipNotify) {
      this.clickSubject.next(null);
    }
  }


  public onSelectingDelivery(): void {
    this.dialog.open(DeliveryComponent, {
      panelClass: 'as-delivery-dialog'
    }).afterClosed().subscribe(response => {
      if (response.status) {
        this.onEmitDisplay(response);
      }
    });
  }

  public onDisplayCart(): void {
    this.dialog.open(CartComponent, {
      panelClass: ['as-cart-dialog', 'animate__animated', 'animate__slideInRight'],
      hasBackdrop: true,
      position: { right: 'right' },
      minHeight: '100vh',
      autoFocus: false
    });
  }

  public async onEmitReload(): Promise<void> {

    this.cartObserver.cart$.emit(true);
  }
  public onDisplaySuccessPopup(): void {
    this.snackbarService.openSnackbar('GENERAL.addingProduct', 'success');
  }

  public onEmitDisplay(response: any): void {
    this.addressObserver.address$.emit(response);
  }

  public async addProductToFavouriteList(): Promise<any> {
    this.loading = true;
    try {
      const data = {
        items: [
          {
            sku: this.product.item_id,
            quantity: 1,
            format: {
              id: this.product.item_id,
              quantity: 1,
              unit: this.productTypeQuantity
            }
          }
        ]
      };
      await this.listsService.addProductToFavouritesList(data);
      this.loading = false;
      this.snackbarService.openSnackbar('GENERAL.productAddedToList', 'success');
      this.product.showHeart = true;
    } catch (error) {
      this.snackbarService.openSnackbar('GENERAL.error', 'warn');
      this.loading = false;
    }
  }

  public async openSureDeleteProductFromList(): Promise<void> {
    try {
      await this.removeProductToList();
    } catch (error) { }
  }

  public async removeProductToList(): Promise<void> {
    try {
      const listArr: any[] = [];
      const res = await this.listsService.getMyLists();
      if (res?.data?.length > 0) {
        res?.data.forEach((el: any) => {
          el?.items?.forEach((element: any) => {
            element?.items.forEach((item: any) => {
              if (item.item_id === this.product.item_id) {
                listArr.push(el.list_id);
              }
            });
          });
        });
      }
      const resp = await this.listsService.getFavouritesLists();
      resp?.data?.items.forEach((el: any) => {
        el?.items?.forEach((item: any) => {
          if (item.item_id === this.product.item_id) {
            listArr.push(resp?.data?.list_id);
          }
        });
      });
      listArr.forEach(async (item: number) => {
        await this.listsService.deleteProductFromList(item, this.product.item_id);
      });
      this.product.showHeart = false;
      this.snackbarService.openSnackbar('GENERAL.productRemovedFromList', 'warn');
    } catch (error) {
      this.snackbarService.openSnackbar('GENERAL.error', 'warn');
    }
  }

  private facebookEvent(eventName: string, uuid: string, customData: EventCustomData) {
    try {
      const pixelEvent: PixelEvent = {
        data: {
          event_name: eventName,
          event_time: Date.now(),
          action_source: 'website',
          event_id: uuid,
          user_data: {
            em: encryptTextSHA256(this.client?.email) ?? '',
            ph: encryptTextSHA256(this.client?.cellphone) ?? '',
            fbp: document.cookie.match('(^|;)\\s*' + '_fbp' + '\\s*=\\s*([^;]+)')?.pop() || '',
            fbc: document.cookie.match('(^|;)\\s*' + '_fbc' + '\\s*=\\s*([^;]+)')?.pop() || '',
            external_id: encryptTextSHA256(this.client?.user_id.toString()) ?? ''
          },
          custom_data: customData,
        }
      };
      this.marketingService.pixelCall(pixelEvent);
    } catch (error) {
    }
  }

  public subtract(): void {
    if (this.productTypeQuantity === 'grammage' || this.productTypeQuantity === 'granel') {
      const quantity = this.productQuantityGrams - this.baseGrams;

      if (quantity <= 0) {
        this.onRemoveProduct();
      } else {
        this.productQuantityGrams -= this.baseGrams;
        if (this.productAdded) {
          this.setProductQuantity();
        }
      }
    } else {
      const quantity = this.productQuantity - 1;

      if (quantity <= 0) {
        this.onRemoveProduct();
      } else {
        this.productQuantity -= 1;
        if (this.productAdded) {
          this.setProductQuantity();
        }
      }
    }
  }

  public async onRemoveProduct(): Promise<void> {
    this.clickSubject.next('skip');
    try {
      this.loading = true;
      await this.cartService.onHandlingRemoveProduct(this.product.item_id);
      this.loading = false;
      this.snackbarService.openSnackbar('GENERAL.deleteProduct', 'success');
      this.onEmitReload();
      this.displayProductQuantity = false;
      this.productAdded = false;
    } catch (error) {
      this.loading = false;
    }
  }


  public async displayDetailsBeforeAdding(): Promise<void> {
    this.setProductQuantity(true);
    if (this.isBrowser) {
      if (localStorage.getItem('token')) {
        if (localStorage.getItem('address')) {
          this.addingProduct = true;
          let productsAux;
          const cartState = await this.storeCart.select('cart').pipe(take(1)).toPromise();
          if (cartState && cartState.info) {
            productsAux = await cartState.info.items.find(((product: any) => product.item_id === this.product.item_id || product.item_id === this.product.id));
          }
          try {
            if (!productsAux) {
              await this.cartService.onAddingNewProduct({
                items: [{
                  itemId: this.product.item_id ?? this.product.id,
                  quantity: this.quantity,
                  branch_id: this.storeId,
                  unit: this.productTypeQuantity,
                  variant: this.madurity.value ? this.madurity.value : this.slice.value
                }]
              });
              this.onEmitReload();
            }
            if (window.location.pathname === '/carrito') {
              this.orderService.refreshHome$.emit(true);
            } else {
              this.onDisplaySuccessPopup();
            }
            if (this.isBrowser) {
              this.sendMarketingData();
            }
            this.displayProductQuantity = true;
            this.addingProduct = false;
          } catch (error: any) {
            this.addingProduct = false;
            if (error?.status === 404) {
              this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
            }
          }
        } else {
          this.dialog.open(DeliveryComponent, {
            panelClass: 'as-delivery-dialog'
          }).afterClosed().subscribe(response => {
            if (response.status) {
              this.onEmitDisplay(response);
            }
          });
        }
      } else {
        this.dialog.open(SigninComponent, {});
      }
    }
  }

  private sendMarketingData() {
    gtag('event', 'add_to_cart', {
      items: [
        {
          id: this.product?.id.toString(),
          name: this.product.name,
          brand: this.product.brand,
          variant: this.product?.price ?? '',
          quantity: 1,
          price: this.product.price.toString()
        }
      ]
    });
    const uuid = uuidv4();
    fbq('track', 'AddToCart', {
      content_ids: [this.product?.id.toString()],
      price: this.product?.price ?? '',
      currency: 'MXN'
    }, { eventID: uuid });
    this.facebookEvent('AddToCart', uuid, {
      id: [this.product?.id.toString()],
      price: this.product?.price ?? '',
      currency: 'MXN'
    });
    //* Pinterest tag
    pintrk('track', 'addtocart', {
      event_id: uuidv4(),
      value: this.product?.price,
      order_quantity: this.product.quantity,
      currency: 'MXN'
    });
  }


  public changePieces(event: any): void {
    if (event) {
      this.productQuantity = parseInt(event);
    }
    if (this.productAdded) {
      this.changePiecesProductAdded(event);
    }
  }

  public add(): void {
    if (this.productTypeQuantity === 'grammage' || this.productTypeQuantity === 'granel') {
      this.productQuantityGrams += this.baseGrams;
    } else {
      this.productQuantity += 1;
    }
    if (this.productAdded) {
      this.setProductQuantity();
    }
  }

  public changeGrams(event: any): void {
    if (event && this.productType !== 'granel') {
      const eventParse = parseInt(event);
      const multiple = (eventParse / this.baseGrams);
      this.productQuantityGrams = Math.round(multiple) * this.baseGrams;
    } else {
      const eventParse = parseInt(event);
      this.productQuantityGrams = eventParse;
    }
    if (this.productAdded) {
      this.changeGramsProductAdded(event);
    }
  }

  public async changeGramsProductAdded(event: any): Promise<void> {
    try {
      if (event) {
        if (this.productType === 'granel') {
          const eventParse = parseInt(event);
          const multiple = eventParse / 1000 / 0.05;
          this.quantity = Math.round(multiple) * 0.05;
        } else {
          const eventParse = parseInt(event);
          const multiple = eventParse / this.productQuantityGrams;
          this.quantity = Math.round(multiple) * this.productQuantityGrams / 1000;
        }

        this.productsValidationsService.editProductInLocalStorage({
          itemId: this.product.item_id,
          quantity: Math.round(this.quantity * 1000) / 1000,
          branch_id: this.storeId,
          unit: this.productTypeQuantity,
          variant: this.madurity.value ? this.madurity.value : this.slice.value
        });
      }
    } catch (error: any) {
      if (error?.status === 404) {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
    }
  }

  public async changePiecesProductAdded(event: any): Promise<void> {
    try {
      if (event) {
        if (this.productType === 'pza' || this.productType === 'granel') {
          this.quantity = parseInt(event);
        } else {
          this.quantity = parseInt(event) * this.productFromCart?.weight;
        }

        this.productsValidationsService.editProductInLocalStorage({
          itemId: this.product.item_id,
          quantity: Math.round(this.quantity * 1000) / 1000,
          branch_id: this.storeId,
          unit: this.productTypeQuantity,
          variant: this.madurity.value ? this.madurity.value : this.slice.value
        });
      }
    } catch (error: any) {
      if (error?.status === 404) {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
    }
  }


  public onSwitchTypeQuanity(type: TProductTypeQuantity): void {
    if (this.productTypeQuantity === type) return;
    if (type === 'pza') {
      this.productQuantity = this.productQuantityGrams / this.baseGrams;
    } else {
      this.productQuantityGrams = this.baseGrams * this.productQuantity;
    }
    this.productTypeQuantity = type;
    if (this.productAdded) {
      this.onAddingToCart();
    }
  }


  public async onAddingToCart(): Promise<any> {
    if (this.isBrowser) {
      if (localStorage.getItem('token')) {
        if (localStorage.getItem('address')) {
          let productsAux;

          const cartState = await this.storeCart.select('cart').pipe(take(1)).toPromise();

          if (cartState && cartState.info) {
            productsAux = await cartState.info.items.find(((product: any) => product.item_id === this.product.item_id || product.item_id === this.product.id));
          }
          try {
            if (productsAux) {
              this.setProductQuantity();
            } else {

              await this.cartService.onAddingNewProduct({
                items: [{
                  itemId: this.product.item_id ?? this.product.id,
                  quantity: this.quantity,
                  branch_id: this.storeId,
                  unit: this.productTypeQuantity,
                  variant: this.madurity.value ? this.madurity.value : this.slice.value
                }]
              });
              this.onDisplaySuccessPopup();

              if (this.isBrowser) {
                this.sendMarketingData();
              }
              this.onEmitReload();
            }


          } catch (error: any) {
            if (error?.status === 404) {
              this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
            }
          }

        } else {
          this.dialog.open(DeliveryComponent, {
            panelClass: 'as-delivery-dialog'
          }).afterClosed().subscribe(response => {
            if (response.status) {
              this.onEmitDisplay(response);
            }
          });
        }
      } else {
        this.dialog.open(SigninComponent, { autoFocus: false });
      }
    }
  }

}
